import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner, Empty } from "@/components/common";
import { useApi, errMsg } from "@/lib/api";

const defaults = (fields) => Object.fromEntries(fields.map((f) => [f.key, f.type === "switch" ? true : f.type === "number" ? 0 : f.options ? f.options[0] : ""]));

const Field = ({ f, value, onChange, resource }) => {
  const id = `${resource}-field-${f.key}`;
  if (f.type === "switch")
    return <label className="flex items-center justify-between rounded-lg bg-[#F7F4EF] p-3 text-sm"><span>{f.label}</span><Switch checked={!!value} onCheckedChange={onChange} data-testid={id} /></label>;
  return (
    <div className="space-y-1.5">
      <Label>{f.label}</Label>
      {f.type === "textarea" ? <Textarea value={value} onChange={(e) => onChange(e.target.value)} data-testid={id} /> :
        f.type === "select" ? (
          <Select dir="rtl" value={value} onValueChange={onChange}>
            <SelectTrigger data-testid={id}><SelectValue /></SelectTrigger>
            <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        ) : (
          <Input dir={f.key === "image" ? "ltr" : undefined} type={f.type || "text"} required={f.required} min={f.type === "number" ? 0 : undefined} step="any" placeholder={f.placeholder} value={value}
            onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} data-testid={id} />
        )}
    </div>
  );
};

export default function EntityManager({ resource, title, fields, columns }) {
  const api = useApi();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaults(fields));
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api("get", `/admin/${resource}`).then(setItems).catch((e) => toast.error(errMsg(e)));
  }, [api, resource]);
  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => { setForm(defaults(fields)); setEditing("new"); };
  const openEdit = (it) => { setForm(Object.fromEntries(fields.map((f) => [f.key, it[f.key] ?? defaults(fields)[f.key]]))); setEditing(it.id); };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" && fields.find((f) => f.key === k).type === "number" ? 0 : v]));
    try {
      if (editing === "new") await api("post", `/admin/${resource}`, body);
      else await api("put", `/admin/${resource}/${editing}`, body);
      toast.success("تم الحفظ");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const del = async (it) => {
    if (!window.confirm(`حذف "${it.name}"؟`)) return;
    try {
      await api("delete", `/admin/${resource}/${it.id}`);
      toast.success("تم الحذف");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div data-testid={`admin-${resource}-manager`}>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#0F382C]">{title}</h2>
        <Button onClick={openNew} className="rounded-full bg-[#0F382C] hover:bg-[#164E3D]" data-testid={`admin-${resource}-add-button`}><Plus className="ml-1 h-4 w-4" />إضافة</Button>
      </div>
      {items === null ? <Spinner /> : items.length === 0 ? <Empty title="لا توجد عناصر" testId={`admin-${resource}-empty`} /> : (
        <div className="overflow-x-auto rounded-2xl border border-[#E5DEC9] bg-white">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-[#F7F4EF] text-[#5C7067]">
              <tr>{columns.map(([k, l]) => <th key={k} className="p-4 text-right font-semibold">{l}</th>)}<th className="p-4 text-right font-semibold">الحالة</th><th className="p-4" /></tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-[#F0EADB]" data-testid={`admin-${resource}-row-${it.id}`}>
                  {columns.map(([k, , fmt]) => <td key={k} className="p-4">{fmt ? fmt(it[k]) : it[k]}</td>)}
                  <td className="p-4"><span className={`rounded-full px-3 py-1 text-xs font-semibold ${it.active ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>{it.active ? "مفعّل" : "موقوف"}</span></td>
                  <td className="p-4">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`admin-${resource}-edit-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-red-600" onClick={() => del(it)} data-testid={`admin-${resource}-delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent dir="rtl" className="max-h-[90vh] overflow-y-auto" data-testid={`admin-${resource}-dialog`}>
          <DialogHeader><DialogTitle className="text-right">{editing === "new" ? "إضافة" : "تعديل"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            {fields.map((f) => <Field key={f.key} f={f} resource={resource} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} />)}
            <Button type="submit" disabled={busy} className="h-11 w-full rounded-full bg-[#0F382C] hover:bg-[#164E3D]" data-testid={`admin-${resource}-save-button`}>{busy ? "جاري الحفظ..." : "حفظ"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
